import type { MinutesAfterMidnight } from "@utils/practice/day-time"
import {
    type CapNotificationType,
    Channel,
    ChannelsAll,
    clearAllNotifications,
    NotificationAtType,
    registerNotifications,
} from "@utils/cap-notification-manager/cap-notification-manager"
import {
    LessonClass,
    type PracticeAny,
    PracticeFrequentReminders,
    PracticeHourlyRemembrance,
    PracticeLessonClass,
    practiceLessonMap,
    PracticeMain,
    PracticeMorningAndEvening,
    PracticePractice,
    PracticeTemptation,
} from "@circleofa/acim-engine"
import { breakdown } from "@utils/practice/reminder-time-logic"
import { addDays, addMinutes, differenceInMinutes, isBefore, startOfDay } from "date-fns"
import { convertCrunchMachine } from "@utils/cap-notification-manager/cap-time-converter"
import { acim } from "@utils/acim"
import { parseMockHTMLAST, stripHtml } from "./cap-mockhtml-parser"
import { createPrompt } from "@components/bottomPopup/Prompt.module"
import { difference, intersection, uniq } from "lodash-es"
import type { ReminderMemoryManagerSettings } from "@utils/practice/reminder-memory-manager"
import { pendingOpenTask } from "../../routes/practice/Task.svelte"
import { goto } from "$app/navigation"

/**
 * If there is a notification that is X minutes before the current time,
 * we just set it to happen now rather than prompting.
 */
const MINUTES_TO_BUFFER_FOR_PROMPT = 60

/**
 *
 * @param title
 * @param body
 * @param day day of the notification (1 = tomorrow, 2 = after tomorrow, etc)
 * @param id
 * @param minutesAfterMidnight
 */
function createTomorrowReminder(
    title: string,
    body: string,
    day: number,
    id: number,
    minutesAfterMidnight: MinutesAfterMidnight,
): CapNotificationType {
    // const debug = addSeconds(new Date(), 30 * day)
    return {
        title,
        body,
        channel: Channel.LESSON_REMINDER,
        id,
        at: {
            frequency: NotificationAtType.EXACT,
            // date: debug,
            date: addMinutes(addDays(startOfDay(new Date()), day), minutesAfterMidnight),
        },
    }
}

export class NextLessonIndicator {
    constructor(public readonly nextLesson: number) {}
}

/**
 *
 * @param practice
 * @throws if temptation
 */
function getChannelFromPractice(practice: PracticeAny): Channel {
    if (practice instanceof PracticeTemptation) throw new Error("Practice temptation not supported")
    if (practice instanceof PracticeFrequentReminders) return Channel.FREQUENT_REMINDERS
    if (practice instanceof PracticeHourlyRemembrance) return Channel.HOURLY_REMEMBRANCE
    if (practice instanceof PracticeMorningAndEvening) return Channel.MAIN_PRACTICE
    if (practice instanceof PracticePractice) return Channel.MAIN_PRACTICE
    if (practice instanceof PracticeMain) return Channel.MAIN_PRACTICE
    console.error(practice)
    throw new Error("Practice not recognised! ")
}

/**
 *
 * @param daysBetween Array containing the number of days between each notification
 * @param nextLessonNumber
 * @returns
 */
function createTomorrowReminderList(daysBetween: number[], nextLessonNumber: number, wake: number) {
    const notifications: CapNotificationType[] = []
    const isEpilogue = nextLessonNumber === 366
    let daysAccumulator = 0
    for (let index = 0; index < daysBetween.length; index++) {
        daysAccumulator += daysBetween[index]
        notifications.push(
            createTomorrowReminder(
                isEpilogue ? "Ready for the epilogue?" : "Ready for your next lesson?",
                isEpilogue
                    ? "You're almost done the workbook!"
                    : `Lesson ${nextLessonNumber} awaits you!`,
                daysAccumulator,
                index,
                wake,
            ),
        )
    }
    return notifications
}

/**
 * Generates title and converts something like "Main Practice" to "Main Practice 2/3" if there are 3 total
 * @param type Practice type like "Main Practice"
 * @param iteration index of array
 * @param channel channel
 * @param total length
 */
function generateTitle(
    type: PracticeAny["type"],
    iteration: number,
    total: number,
    channel: Channel,
    at: NotificationAtType,
): string {
    if (at !== NotificationAtType.EXACT) return type
    if (channel === Channel.LESSON_REMINDER) return type
    if (total === 1) return type

    const actualNumber = iteration + 1
    if (type === "Morning and Evening Practice") {
        return actualNumber === 1 ? "Morning Practice" : "Evening Practice"
    }
    return `${type} ${actualNumber}/${total}`
}

export function getNotificationIDMap(lessonNumber: number) {
    const lessonData = practiceLessonMap.get(lessonNumber)
    // const lesson : LessonClass | null = acim.workbook.getLesson(lessonNumber)

    if (!lessonData) throw new Error("Lesson not found in practice map: " + lessonNumber)
    const practices = breakdown(lessonData)

    /** Epilogue is handled in the {@link createTomorrowReminderList} **/
    const nextLessonNumber = lessonNumber + 1

    // Generate the "next notification" list
    const daysBetween = [1, 1, 1, 7, 7, 14]

    // So our map works properly, this is the ID of the next notification in the list
    const continuingFromId = daysBetween.length

    // Init the map which we should be able to access without needing times and dates and stuff
    // and init with the first ones
    const map = (id: number) => {
        if (id < continuingFromId) return new NextLessonIndicator(nextLessonNumber)

        // This needs to match up with whats in generate
        const todaysNotificationId = id - continuingFromId // If continuingFromId = 6, id 6 becomes 0
        const practiceId = Math.floor(todaysNotificationId / 10)

        const practice = [...practices.keys()].at(practiceId)
        if (!practice) throw new Error(`Practice not found`)
        return practice
    }

    const generate = (
        /**
         * What time to wake up for tomorrows alarm
         */
        wake: MinutesAfterMidnight,
        /**
         * Earliest you can have a practice
         */
        bottomLimit: MinutesAfterMidnight,
        /**
         * Latest you can have a practice
         */
        topLimit: MinutesAfterMidnight,
        /**
         * What channels to include
         */
        channels: readonly Channel[],
        memoryManager: ReminderMemoryManagerSettings,
    ): CapNotificationType[] => {
        // This generates notifications that lack the ID, which is added afterward
        const processed: CapNotificationType[] = [...practices].flatMap(
            ([practice, timing], practiceId) => {
                const converted = convertCrunchMachine(timing, bottomLimit, topLimit, memoryManager)
                // TODO implement temptations
                if (converted === "Temptation") return [] // TODO implement temptations - persistent notifications
                if (converted === null) return [] // some tasks don't actually have notifications

                const channel = getChannelFromPractice(practice)

                // If we have this channel disabled, return an empty array
                if (!channels.includes(channel)) return []

                // Must match with practiceLessonId at the top
                const practiceIdx10 = practiceId * 10

                return converted.map((time, individualReminderIdForPractice) => {
                    if (individualReminderIdForPractice > 10)
                        throw new Error("Individual practice Id must be less than 10")

                    const id = practiceIdx10 + individualReminderIdForPractice + continuingFromId
                    if (id >= 300) throw new Error("Practice ID must be less than 300")

                    return {
                        at: time,
                        id,
                        title: generateTitle(
                            practice.type,
                            individualReminderIdForPractice,
                            converted.length,
                            channel,
                            time.frequency,
                        ),
                        html: practice.instruction
                            ? {
                                  html: practice.instruction,
                                  htmlAst: parseMockHTMLAST(practice.instruction),
                              }
                            : undefined,
                        body: stripHtml(practice.body),
                        channel,
                    } satisfies CapNotificationType
                })
            },
        )

        // Create the next notifications, but only if its selected
        const tomorrowsReminders = memoryManager.channelIsEnabled(Channel.LESSON_REMINDER)
            ? createTomorrowReminderList(daysBetween, nextLessonNumber, wake)
            : []
        return [...tomorrowsReminders, ...processed]
    }

    return { generate, map }
}

/**
 * How many minutes since the notification passed (useful for assessing if we should display it anyway!)
 * @throws
 * @returns number of minutes since the notification has passed, negative if it is in the future, null if its not an exact alarm
 */
function notificationMinutesElapsed(notf: CapNotificationType, now = new Date()): number | null {
    if (notf.at.frequency !== NotificationAtType.EXACT) return null
    const date = notf.at.date
    if (!date) throw new Error("Expecting date, none supplied")
    return differenceInMinutes(now, date)
}

/**
 * Filters all notifications and only shows if they have past and are exact
 * @throws
 */
function notificationHasAlreadyPassed(notf: CapNotificationType, now = new Date()): boolean {
    const elapsed = notificationMinutesElapsed(notf, now)
    if (elapsed === null) return false
    return elapsed > 0
}

/**
 * Returns a function that gives information for prompting
 *
 * @param lesson current lesson to set
 * @param wake minutes after midnight for wakeup time
 * @param sleep minutes after midnight for sleep time
 * @param bottomLimit earliest you can have a reminder
 * @param topLimit latest you can have a reminder
 * @param channels sets channels, leave blank or set to null for all channels
 *
 * @throws might throw
 */
export async function setLessonReminders(
    lesson: number,
    wake: MinutesAfterMidnight,
    sleep: MinutesAfterMidnight,
    bottomLimit: MinutesAfterMidnight,
    topLimit: MinutesAfterMidnight,
    memoryManager: ReminderMemoryManagerSettings,
    allowedChannels: Channel[],
) {
    // Clear originals
    await clearAllNotifications()

    const { generate, map } = getNotificationIDMap(lesson)
    const channels = intersection(memoryManager.channelsEnabled, allowedChannels)
    const notifications = generate(wake, bottomLimit, topLimit, channels, memoryManager)

    console.log("Registering: ", channels)

    // We can assume that these will generally always be the same

    // Find the notifications that are older than MINUTES_TO_BUFFER
    const notfsInLastHour = notifications.filter((notf) => {
        const mins = notificationMinutesElapsed(notf)
        if (mins === null) return false
        return mins > 0 && mins <= MINUTES_TO_BUFFER_FOR_PROMPT
    })

    // Find the notifications that are older than MINUTES_TO_BUFFER
    const notfsBeforeOneHour = notifications.filter((notf) => {
        const mins = notificationMinutesElapsed(notf)
        if (mins === null) return false
        return mins > MINUTES_TO_BUFFER_FOR_PROMPT
    })

    // Unique array of anything over an hour
    const notfsPassed = uniq([...notfsInLastHour, ...notfsBeforeOneHour])

    const pastOneHourRemindersExist = notfsBeforeOneHour.length > 0
    const now = new Date()
    const dayStart = startOfDay(now)

    // DEPRECATED!
    //
    // Function that sets all notifications on the device
    // const setAll = () =>
    //     registerNotifications({
    //         lesson,
    //         until: addMinutes(dayStart, sleep),
    //         notifications,
    //     })

    const remainder = difference(notifications, notfsPassed)
    // Function that only sets future notifications (ignoring past notifications)
    const setFutureOnly = async () => {
        await registerNotifications({
            lesson,
            until: addMinutes(dayStart, sleep),
            notifications: remainder,
        })

        // Navigates the user to the instructions of the prompted task
        // The reason why we only want to do this if there are no past
        // one hour reminders exist is because in the prompt, if there
        // are past reminders, and the user clicks skip, they should not
        // be notified in any way
        if (pastOneHourRemindersExist) return
        if (notfsInLastHour.length) {
            const id = notfsInLastHour[0].id
            const at = map(id)
            if (at instanceof NextLessonIndicator)
                throw new Error(`Not expecting next lesson indicator`)
            pendingOpenTask.set(at)
            goto("/practice")
        }
    }

    const setAll = async () => {
        await registerNotifications({
            lesson,
            until: addMinutes(dayStart, sleep),
            notifications: remainder,
        })

        // Navigates the user to the instructions of the prompted task
        if (notfsPassed.length) {
            const id = notfsPassed[0].id
            const at = map(id)
            if (at instanceof NextLessonIndicator)
                throw new Error(`Not expecting next lesson indicator`)
            pendingOpenTask.set(at)
            goto("/practice")
        }
    }

    return {
        number: notfsPassed.length, // This includes ALL older notifications before the current moment
        pastRemindersExist: pastOneHourRemindersExist, // This determines whether we show the dialog, which is only if there are notifications >1 hr
        setAll,
        setFutureOnly,
    }
}
